import { useMemo, useState } from 'react';
import { PageGroup } from '../types/meta.types';
import { Boxes, Plus, Pencil, Trash2, Search, Layers, AppWindow, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';

interface PageGroupsListProps {
    groups: PageGroup[];
    onCreate: () => void;
    onEdit: (group: PageGroup) => void;
    onDelete: (group: PageGroup) => void;
}

export default function PageGroupsList({ groups, onCreate, onEdit, onDelete }: PageGroupsListProps) {
    const [searchTerm, setSearchTerm] = useState('');

    const filteredGroups = useMemo(() => {
        if (!searchTerm) return groups;
        const q = searchTerm.toLowerCase();
        return groups.filter(g =>
            g.name.toLowerCase().includes(q) ||
            g.app_name.toLowerCase().includes(q)
        );
    }, [groups, searchTerm]);

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">Grupos de Páginas</h1>
                    <p className="mt-2 text-gray-600 max-w-2xl">
                        Cada grupo aponta pra um app. Páginas no grupo enviam usando o token desse app no broadcast.
                        Páginas sem grupo usam o primeiro app conectado.
                    </p>
                </div>
                <Button
                    onClick={onCreate}
                    className="bg-[#0f172a] hover:bg-[#1e293b] text-white font-medium px-6 py-2.5 rounded-xl"
                >
                    <Plus className="mr-2 h-4 w-4" />
                    Novo Grupo
                </Button>
            </div>

            {/* Filtros */}
            {groups.length > 0 && (
                <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                    <div className="flex-1 space-y-1.5">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider ml-1">
                            Buscar
                        </label>
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <Input
                                placeholder="Nome do grupo ou app..."
                                className="pl-9"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Lista */}
            {groups.length === 0 ? (
                <div className="bg-white rounded-lg p-16 text-center shadow border border-gray-100">
                    <div className="mx-auto w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-6">
                        <Boxes className="w-8 h-8 text-blue-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Nenhum grupo cadastrado</h3>
                    <p className="text-gray-500 mb-6 max-w-sm mx-auto">
                        Crie grupos pra rotear envios de páginas específicas pra apps diferentes.
                    </p>
                    <Button onClick={onCreate} variant="outline" className="border-blue-200 text-blue-600 hover:bg-blue-50">
                        Criar primeiro grupo
                    </Button>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredGroups.map((group) => {
                        const orphans = group.pages.filter(p => !p.has_token_for_group_app).length;
                        return (
                            <div
                                key={group.id}
                                className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden"
                            >
                                <div className="p-5 space-y-3">
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="flex items-center gap-3 min-w-0">
                                            <div className="h-10 w-10 rounded-lg bg-indigo-50 flex items-center justify-center shrink-0">
                                                <Boxes className="h-5 w-5 text-indigo-600" />
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-sm font-semibold text-gray-900 truncate">{group.name}</p>
                                                <p className="text-xs text-gray-500">
                                                    Criado em {new Date(group.created_at).toLocaleDateString('pt-BR')}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => onEdit(group)}
                                                className="h-8 w-8 p-0 text-gray-500 hover:text-blue-600"
                                                title="Editar"
                                            >
                                                <Pencil className="h-4 w-4" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => onDelete(group)}
                                                className="h-8 w-8 p-0 text-gray-500 hover:text-red-600"
                                                title="Excluir"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2 text-sm">
                                        <AppWindow className="h-4 w-4 text-gray-400" />
                                        <span className="text-gray-600">App:</span>
                                        <Badge variant="outline" className="font-medium text-violet-700 border-violet-200 bg-violet-50/50">
                                            {group.app_name}
                                        </Badge>
                                    </div>

                                    <div className="flex items-center gap-2 text-sm">
                                        <Layers className="h-4 w-4 text-gray-400" />
                                        <span className="text-gray-600">Páginas:</span>
                                        <span className="font-semibold text-gray-900">{group.total_pages}</span>
                                    </div>

                                    {orphans > 0 && (
                                        <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                                            <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                                            <span>
                                                <strong>{orphans}</strong> página{orphans !== 1 ? 's' : ''} sem token nesse app — será{orphans !== 1 ? 'ão' : ''} pulada{orphans !== 1 ? 's' : ''} no envio.
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
